import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { sendStackShareEmail } from '../../../services/emailService';
import { supabase } from '../../../lib/supabase';

const ConfirmationPopup = ({ recipientEmail, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 3500);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 pointer-events-none">
      <div
        className="pointer-events-auto relative flex flex-col items-center gap-3 bg-white rounded-2xl shadow-2xl px-8 py-7 animate-fade-in"
        style={{ border: '1.5px solid #bbf7d0', minWidth: 300, maxWidth: 360 }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 w-7 h-7 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
        >
          <Icon name="X" size={14} color="#6b7280" />
        </button>

        {/* Icon */}
        <div
          className="w-16 h-16 rounded-full flex items-center justify-center"
          style={{ background: 'linear-gradient(135deg, #22c55e, #16a34a)' }}
        >
          <Icon name="CheckCircle" size={30} color="white" />
        </div>

        {/* Text */}
        <div className="text-center">
          <h3 className="text-lg font-bold text-gray-900 mb-1">Email Sent!</h3>
          <p className="text-sm text-gray-500 leading-relaxed">
            Your stack comparison has been shared with{' '}
            <span className="font-semibold text-gray-700">{recipientEmail}</span>.
          </p>
        </div>

        {/* Progress bar */}
        <div className="w-full h-1 rounded-full bg-gray-100 overflow-hidden mt-1">
          <div
            className="h-full rounded-full"
            style={{
              background: 'linear-gradient(90deg, #22c55e, #16a34a)',
              animation: 'shrink 3.5s linear forwards',
            }}
          />
        </div>

        <style>{`
          @keyframes shrink {
            from { width: 100%; }
            to { width: 0%; }
          }
          @keyframes fade-in {
            from { opacity: 0; transform: scale(0.92) translateY(8px); }
            to { opacity: 1; transform: scale(1) translateY(0); }
          }
          .animate-fade-in {
            animation: fade-in 0.25s ease-out forwards;
          }
        `}</style>
      </div>
    </div>
  );
};

const ShareStackModal = ({ isOpen, onClose, project, quotes }) => {
  const [recipientEmail, setRecipientEmail] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const shareUrl = `${window.location?.origin}/project-comparison?project=${project?.id}`;

  const handleSend = async () => {
    if (!recipientEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(recipientEmail)) {
      setError('Please enter a valid email address.');
      return;
    }
    setSending(true);
    setError('');
    try {
      const { data: sessionData } = await supabase?.auth?.getSession();
      const senderEmail = sessionData?.session?.user?.email || '';
      const senderName = senderEmail?.split('@')?.[0] || 'A Stackd user';

      const options = quotes?.slice(0, 10)?.map(q => ({
        name: q?.vendor?.name || 'Unknown',
        price: q?.price ?? null,
      }));

      const result = await sendStackShareEmail(recipientEmail, {
        projectName: project?.name,
        projectCategory: project?.category,
        sharedByName: senderName,
        sharedByEmail: senderEmail,
        options,
        notes: message,
        shareUrl,
      });

      if (result?.success) {
        setSent(true);
        setShowConfirmation(true);
      } else {
        setError(result?.error || 'Failed to send email. Please try again.');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    setRecipientEmail('');
    setMessage('');
    setSent(false);
    setShowConfirmation(false);
    setError('');
    onClose();
  };

  const handleConfirmationClose = () => {
    setShowConfirmation(false);
    handleClose();
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />
        {/* Modal */}
        <div
          className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
          style={{ border: '1.5px solid #e9d5ff' }}
        >
          {/* Header */}
          <div
            className="px-6 py-5"
            style={{ background: 'linear-gradient(135deg, #7c3aed, #a855f7, #ec4899)' }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/20 border border-white/30 flex items-center justify-center">
                  <Icon name="Share2" size={20} color="white" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white">Share Stack</h2>
                  <p className="text-white/80 text-xs mt-0.5">Send this comparison via email</p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
              >
                <Icon name="X" size={16} color="white" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="px-6 py-5">
            {sent ? (
              <div className="text-center py-6">
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                  style={{ background: 'linear-gradient(135deg, #22c55e, #16a34a)' }}
                >
                  <Icon name="CheckCircle" size={28} color="white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Email Sent!</h3>
                <p className="text-sm text-gray-500">
                  Your comparison for <strong>{project?.name}</strong> has been shared with{' '}
                  <strong>{recipientEmail}</strong>.
                </p>
                <button
                  onClick={handleClose}
                  className="mt-6 px-6 py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90"
                  style={{ background: 'linear-gradient(135deg, #7c3aed, #a855f7)' }}
                >
                  Done
                </button>
              </div>
            ) : (
              <>
                {/* Stack preview */}
                <div
                  className="rounded-xl p-3.5 mb-5 flex items-center gap-3"
                  style={{ background: '#faf5ff', border: '1px solid #e9d5ff' }}
                >
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: 'linear-gradient(135deg, #7c3aed, #a855f7)' }}
                  >
                    <Icon name="Layers" size={16} color="white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{project?.name || 'Untitled Stack'}</p>
                    <p className="text-xs text-gray-500">{quotes?.length || 0} option{quotes?.length !== 1 ? 's' : ''} · {project?.category || 'General'}</p>
                  </div>
                </div>

                {/* Recipient */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Recipient Email <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="email"
                    value={recipientEmail}
                    onChange={(e) => { setRecipientEmail(e?.target?.value); setError(''); }}
                    placeholder="friend@example.com"
                    className="w-full px-4 py-3 rounded-xl border text-sm bg-white text-gray-900 placeholder-gray-400 transition-all outline-none focus:ring-2"
                    style={{
                      borderColor: error ? '#f87171' : '#e9d5ff',
                      boxShadow: error ? '0 0 0 2px rgba(248,113,113,0.15)' : undefined,
                    }}
                  />
                  {error && <p className="mt-1.5 text-xs text-red-500">{error}</p>}
                </div>

                {/* Message */}
                <div className="mb-5">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Message <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e?.target?.value)}
                    placeholder="Add a personal note..."
                    rows={3}
                    className="w-full px-4 py-3 rounded-xl border text-sm bg-white text-gray-900 placeholder-gray-400 transition-all outline-none focus:ring-2 resize-none"
                    style={{ borderColor: '#e9d5ff' }}
                  />
                </div>

                {/* Options preview */}
                {quotes?.length > 0 && (
                  <div className="mb-5">
                    <p className="text-xs font-medium text-gray-500 mb-2">Options included in email:</p>
                    <div className="flex flex-wrap gap-1.5">
                      {quotes?.slice(0, 5)?.map((q, i) => (
                        <span
                          key={i}
                          className="text-xs px-2.5 py-1 rounded-full font-medium"
                          style={{ background: '#faf5ff', color: '#7c3aed', border: '1px solid #e9d5ff' }}
                        >
                          {q?.vendor?.name || `Option ${i + 1}`}
                        </span>
                      ))}
                      {quotes?.length > 5 && (
                        <span className="text-xs px-2.5 py-1 rounded-full text-gray-400" style={{ background: '#f9fafb' }}>
                          +{quotes?.length - 5} more
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleClose}
                    className="flex-1 py-2.5 rounded-xl border text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors"
                    style={{ borderColor: '#e5e7eb' }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSend}
                    disabled={sending || !recipientEmail}
                    className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    style={{ background: 'linear-gradient(135deg, #7c3aed, #a855f7)' }}
                  >
                    {sending ? (
                      <>
                        <Icon name="Loader" size={14} color="white" className="animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Icon name="Send" size={14} color="white" />
                        Send Email
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation popup */}
      {showConfirmation && (
        <ConfirmationPopup
          recipientEmail={recipientEmail}
          onClose={handleConfirmationClose}
        />
      )}
    </>
  );
};

export default ShareStackModal;
